import { Record as ImmutableRecord, RecordOf } from 'immutable';

import { matchStateInBetFromBackend } from 'app/api/services/active-stakes/types';
import type { CurrencyCode } from 'app/api/services/site-configs/types';

import type { BackendTimer, RecordOfMatchEntity } from 'app/lib/betting/betting-entities';
import { MatchStatus } from 'app/lib/betting/betting-entities';
import {
  BetState,
  CashoutErrorCode,
  CashoutMode,
  StakeCashoutState,
  StakeState,
  StakeType,
  SystemState,
} from 'app/lib/staking/constants';

import { EntriesStateType } from './constants';

// ---

type StakeChain = {
  id: number | null;
  isChain: boolean;
  hasLiveItems: boolean;
  items: RecordOfStake[];
  createdAt: Date | null;
  settledAt: Date | null;
};

export type RecordOfStakeChain = RecordOf<StakeChain>;

export const StakeChainEntity = ImmutableRecord<StakeChain>({
  id: null,
  isChain: true,
  hasLiveItems: false,
  items: [],
  createdAt: null,
  settledAt: null,
}, 'StakeChainEntity');

// ---

export type Stake = {
  id: number | null;
  type: StakeType | null;
  isFreeBet: boolean;
  isLive: boolean;
  rank: number | null;
  amount: number | null;
  price: number;
  winning: number;
  createdAt: Date | null;
  settledAt: Date | null;
  currency?: CurrencyCode;
  state: StakeState | null;
  status: symbol | null;
  bets: RecordOfStakeBet[];
  chainId: number | null;
  position: number | null;
  possibleAmount: number | null;
  debitedToBank: number | null;
  cashoutMode: CashoutMode;
  cashoutAmount: number | null;
  cashoutState: StakeCashoutState;
  cashoutNamespace: EntriesStateType | null;
  error: RecordOfStakeError | null;
  matchPrices: Record<string, number> | null;
  systemStatus: Record<SystemState, number> | null;
  matchStatuses: Record<string, BetState> | null;
};

export type RecordOfStake = RecordOf<Stake>;

export const StakeEntity = ImmutableRecord<Stake>({
  id: null,
  type: null,
  isFreeBet: false,
  isLive: false,
  rank: null,
  amount: null,
  price: 0,
  winning: 0,
  createdAt: null,
  settledAt: null,
  currency: undefined,
  state: null,
  status: null,
  bets: [],
  chainId: null,
  position: null,
  possibleAmount: null,
  debitedToBank: null,
  cashoutMode: CashoutMode.PROHIBITED,
  cashoutAmount: null,
  cashoutState: StakeCashoutState.AWAITING,
  cashoutNamespace: null,
  error: null,
  matchPrices: null,
  systemStatus: null,
  matchStatuses: null,
}, 'StakeEntity');

// ---

type StakeBet = {
  id: number | null;
  isLive: boolean;
  isInLive: boolean;
  matchId: number | null;
  sportId: number | null;
  unionId: number | null;
  tournamentId: number | null;
  stageId: number | null;
  state: BetState | null;
  status: symbol | null;
  slotType: number | null;
  matchName: string;
  stakeName: string;
  betName: string;
  matchStartDate: Date | null;
  matchStatus: MatchStatus | null;
  matchState: matchStateInBetFromBackend | null;
  price: number | null;
  cancelReason: string | null;
  isShown: boolean;
  periods: RecordOfMatchEntity['periods'] | null;
  scores: RecordOfMatchEntity['scores'] | null;
  timer: BackendTimer | null;
  serveTeam?: number;
};

export type RecordOfStakeBet = RecordOf<StakeBet>;

export const StakeBetEntity = ImmutableRecord<StakeBet>({
  id: null,
  isLive: false,
  isInLive: false,
  matchId: null,
  sportId: null,
  unionId: null,
  tournamentId: null,
  stageId: null,
  state: null,
  status: null,
  slotType: null,
  matchName: '',
  stakeName: '',
  betName: '',
  matchStartDate: null,
  matchStatus: null,
  matchState: null,
  price: null,
  cancelReason: null,
  isShown: false,
  periods: null,
  scores: null,
  timer: null,
  serveTeam: 0,
}, 'StakeBetEntity');

// ---

type StakeError = {
  code: CashoutErrorCode | null;
  message: string;
  prevAmount: number | null;
};

export type RecordOfStakeError = RecordOf<StakeError>;

export const StakeErrorEntity = ImmutableRecord<StakeError>({
  code: null,
  message: '',
  prevAmount: null,
}, 'StakeErrorEntity');

// ---

export const isChain = (
  stake: RecordOfStake | RecordOfStakeChain,
): stake is RecordOfStakeChain => {
  return 'isChain' in stake;
};
